// src/components/utils/PdfUploadField.jsx
import React, { useState, useId } from 'react';
import { getUploadUrl } from '../../Api'; 

export default function PdfUploadField({
  label,
  file,
  onChange,
  onRemove,
  disabled          // ← true quando il form è in sola-lettura
}) {
  const id = useId();

  /* ───────── stato locale (solo simulazione upload) ───────── */
  const [isUploading,    setIsUploading]    = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [justUploaded,   setJustUploaded]   = useState(false);

  /* ───────── tipi di “file” possibili ───────── */
  const isUploaded  = !!file && typeof file === 'string';
  const isLocalFile = !!file && typeof file === 'object';

  const publicUrl = isUploaded
    ? (file.includes('/') ? file : getUploadUrl(file)) 
    : null;

  /* ───────── selezione di un nuovo PDF ───────── */
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    setIsUploading(true);
    setUploadProgress(0);
    e.target.value = '';            // reset input

    // finta progress-bar
    const iv = setInterval(() => {
      setUploadProgress((p) => {
        if (p >= 100) {
          clearInterval(iv);
          setIsUploading(false);
          setJustUploaded(true);
          onChange(selected);       // propaga il File
          setTimeout(() => setJustUploaded(false), 2000);
          return 100;
        }
        return p + Math.random() * 30;
      });
    }, 100);
  };

  /* ───────── elimina file ───────── */
  const handleRemove = () => {
    setJustUploaded(false);
    setUploadProgress(0);
    onChange(null);
    onRemove?.();
  };

  /* ───────── download file ───────── */
  const handleDownload = () => {
    let url, name;
    if (isUploaded) {
      url  = publicUrl;
      name = file.split('/').pop();
    } else if (isLocalFile) {
      url  = URL.createObjectURL(file);
      name = file.name;
    } else return;

    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (isLocalFile) URL.revokeObjectURL(url);
  };

  /* ───────── anteprima ───────── */
  const handlePreview = () => {
    if (isUploaded) {
      window.open(publicUrl, '_blank');
    } else if (isLocalFile) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      // in un’app reale revocheresti l’URL onclose
    }
  };

  /* ───────── stile base ───────── */
  const labelBase = 'block font-medium text-green-500 flex items-center gap-2';
  const areaStyle = disabled
    ? 'border-gray-600 text-gray-500 cursor-not-allowed'
    : 'border-gray-600 text-gray-300 hover:border-green-500 hover:text-green-400 hover:bg-green-500/5';

  /* ───────── render ───────── */
  return (
    <div className="flex flex-col space-y-2">
      {/* LABEL campo */}
      <label className={labelBase}>
        <span className="inline-block">📄</span>{label}
      </label>

      {/* AREA UPLOAD (nascosta se read-only) */}
      {!file && !isUploading && !disabled && (
        <div className="relative">
          <input
            type="file"
            accept="application/pdf"
            disabled={disabled}
            onChange={handleFileChange}
            className="sr-only"
            id={`pdf-upload-${id}`}
          />
          <label
            htmlFor={`pdf-upload-${id}`}
            className={`w-full flex items-center justify-center gap-3 px-6 py-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${areaStyle}`}
          >
            <span className="text-2xl">⬆️</span>
            <div className="text-center">
              <div className="font-medium">Carica file PDF</div>
              <div className="text-sm text-gray-500">Clicca per selezionare o trascina qui</div>
            </div>
          </label>
        </div>
      )}

      {/* PROGRESS BAR */}
      {isUploading && (
        <div className="flex items-center justify-between bg-blue-800/20 border-blue-500 text-blue-300 px-4 py-3 rounded-lg border">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className="text-xl flex-shrink-0 animate-spin">⏳</span>
            <div className="truncate">
              <div className="font-medium">Caricamento in corso…</div>
              <div className="text-sm text-blue-400">{Math.round(uploadProgress)}%</div>
            </div>
          </div>
          <div className="ml-4 w-24 bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* CARD FILE CARICATO / GIÀ PRESENTE */}
      {file && !isUploading && (
        <div className={`
          flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-300
          ${justUploaded ? 'bg-green-800/20 border-green-500 text-green-300'
                         : 'bg-gray-800 border-gray-600 text-white'}
        `}>
          {/* info file */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <span className={`text-xl flex-shrink-0 ${justUploaded ? 'animate-pulse' : ''}`}>
              {justUploaded ? '✅' : '📑'}
            </span>
            <div className="truncate">
              <div className="font-medium truncate">
                {isUploaded ? file.split('/').pop() : file.name}
              </div>
              <div className="text-sm text-gray-400 flex items-center gap-2">
                {isLocalFile && file.size
                  ? `${(file.size / 1024 / 1024).toFixed(2)} MB`
                  : 'PDF'}
                {justUploaded && <span className="text-green-400">✓ Caricato!</span>}
              </div>
            </div>
          </div>

          {/* azioni */}
          <div className="flex items-center gap-2 ml-4">
            {/* SEMPRE visibili */}
            <button
              onClick={handlePreview}
              className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 rounded"
              title="Anteprima"
            >
              <span className="text-sm">👁️</span>
            </button>
            <button
              onClick={handleDownload}
              className="p-2 text-green-400 hover:text-green-300 hover:bg-green-400/10 rounded"
              title="Scarica"
            >
              <span className="text-sm">⬇️</span>
            </button>

            {/* SOLO se non è read-only */}
            {!disabled && (
              <button
                onClick={handleRemove}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded"
                title="Rimuovi"
              >
                <span className="text-sm">✕</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
